/**
 * Avatar Service for Poker Reborn
 *
 * Manages the hero's avatar selection. Persists to localStorage,
 * syncs with backend profile service (when available).
 *
 * @see src/config/avatars.ts - Avatar ID ↔ asset path mapping
 * @see tasks/active/T32_AVATAR_RESEARCH.md - Avatar system design
 */

import { EventEmitter } from './EventEmitter';
import { storageService } from './StorageService';
import { networkService } from './NetworkService';
import { ServiceGLIDs, Commands } from '@/types/protocol';
import { getAvatarPath, isValidAvatarId } from '@/config/avatars';
import { appState } from '@/state/AppState';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

export interface AvatarServiceEvents {
  /** Hero's avatar changed */
  avatarChanged: { avatarId: number };
}

// =============================================================================
// AVATAR SERVICE
// =============================================================================

export class AvatarService extends EventEmitter<AvatarServiceEvents> {
  private static instance: AvatarService | null = null;

  /** Cached hero avatar ID (null = not selected) */
  private cachedAvatarId: number | null = null;

  private constructor() {
    super();
  }

  /** Get singleton instance */
  static getInstance(): AvatarService {
    if (!AvatarService.instance) {
      AvatarService.instance = new AvatarService();
    }
    return AvatarService.instance;
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get the hero's current avatar ID, or null if not selected */
  getAvatarId(): number | null {
    return this.cachedAvatarId;
  }

  /** Get the hero's avatar asset path, or null if not selected */
  getAvatarPath(): string | null {
    return this.cachedAvatarId !== null ? getAvatarPath(this.cachedAvatarId) : null;
  }

  /** Whether the hero has selected an avatar */
  hasAvatar(): boolean {
    return this.cachedAvatarId !== null;
  }

  /**
   * Set the hero's avatar. Saves to localStorage immediately,
   * sends update to backend (fire-and-forget), emits event.
   */
  setAvatar(avatarId: number): void {
    if (!isValidAvatarId(avatarId)) {
      log.avatar.warning`Invalid avatar ID: ${String(avatarId)}`;
      return;
    }

    this.cachedAvatarId = avatarId;
    storageService.set('user.avatarId', avatarId);
    appState.playerAvatarId.set(avatarId);

    log.avatar.info`Avatar set to ${String(avatarId)}`;
    this.emit('avatarChanged', { avatarId });

    // Fire-and-forget to backend (may not be ready yet)
    this.sendToBackend(avatarId);
  }

  /**
   * Load hero's avatar from localStorage on boot.
   * Call this after authentication.
   */
  loadFromStorage(): void {
    const stored = storageService.get('user.avatarId');
    if (stored !== undefined && isValidAvatarId(stored)) {
      this.cachedAvatarId = stored;
      appState.playerAvatarId.set(stored);
      log.avatar.debug`Loaded avatar from storage: ${String(stored)}`;
    }
  }

  /**
   * Update from backend profile data. Backend value wins on conflict.
   * Called when server sends profile data with avatar info.
   */
  updateFromProfile(profileData: Record<string, unknown> | undefined): void {
    const avatarId = AvatarService.resolveAvatarId(profileData);
    if (avatarId === null) return;

    if (avatarId !== this.cachedAvatarId) {
      this.cachedAvatarId = avatarId;
      storageService.set('user.avatarId', avatarId);
      appState.playerAvatarId.set(avatarId);
      log.avatar.debug`Avatar updated from backend: ${String(avatarId)}`;
      this.emit('avatarChanged', { avatarId });
    }
  }

  // ===========================================================================
  // STATIC HELPERS
  // ===========================================================================

  /**
   * Extract avatar ID from any player's profile data.
   * Works for hero and opponents at the table.
   */
  static resolveAvatarId(profileData: Record<string, unknown> | undefined): number | null {
    if (!profileData) return null;
    const avatar = profileData.avatar;
    return isValidAvatarId(avatar) ? avatar : null;
  }

  // ===========================================================================
  // PRIVATE
  // ===========================================================================

  /** Send avatar update to backend profile service (fire-and-forget) */
  private sendToBackend(avatarId: number): void {
    try {
      networkService.send(ServiceGLIDs.ACCOUNT, Commands.setAvatar(avatarId));
      log.avatar.debug`Sent avatar update to backend: ${String(avatarId)}`;
    } catch {
      // Backend may not support this yet — that's fine
      log.avatar.debug`Failed to send avatar to backend (may not be implemented yet)`;
    }
  }
}

export const avatarService = AvatarService.getInstance();
